// Contexto "support" — pedidos de ajuda (tickets), docs/PLATAFORMA.md §7.3. Regras puras, sem base de dados:
// usadas pelo backend (decide), pelo backoffice e pelo site (mostram o que é possível e os prazos).

import { activeLangOr, ACTIVE_LANGS, type Lang } from "@ecliptica/i18n/langs";

/* ------------------------------------------------------------------ respostas guardadas (uma coluna por língua) */

/** Coluna de cada língua numa resposta guardada. */
export const CANNED_BODY = { pt: "bodyPt", en: "bodyEn" } as const satisfies Record<Lang, string>;
export type CannedBodies = { bodyPt: string; bodyEn: string };

/** Texto a inserir num pedido: o da língua do pedido se estiver ativa, senão o da língua por omissão. Nunca uma inativa. */
export function cannedBody(c: CannedBodies, ticketLocale: unknown): string {
  return c[CANNED_BODY[activeLangOr(ticketLocale)]];
}

/** Uma resposta guardada só precisa de texto nas línguas ATIVAS (as inativas são opcionais e mantêm o valor guardado). */
export function cannedComplete(c: Partial<CannedBodies>): boolean {
  return ACTIVE_LANGS.every((l) => (c[CANNED_BODY[l]] ?? "").trim().length > 0);
}

export const CATEGORIES = ["account", "purchases", "bug", "report_player", "suggestion", "other"] as const;
export type Category = (typeof CATEGORIES)[number];

export const PRIORITIES = ["low", "normal", "high", "urgent"] as const;
export type Priority = (typeof PRIORITIES)[number];

export const STATUSES = ["open", "in_progress", "waiting_player", "resolved", "closed"] as const;
export type Status = (typeof STATUSES)[number];

export const SOURCES = ["site", "game", "email"] as const;
export type Source = (typeof SOURCES)[number];

/** Estados em que o pedido está "vivo" (conta para a fila e para os prazos). */
export const ACTIVE_STATUSES = ["open", "in_progress", "waiting_player"] as const satisfies readonly Status[];
export function isActive(s: Status): boolean {
  return (ACTIVE_STATUSES as readonly string[]).includes(s);
}

/** Prioridade inicial por categoria (a equipa pode mudar depois). Denúncias e compras pesam mais. */
export const DEFAULT_PRIORITY: Record<Category, Priority> = {
  account: "high",
  purchases: "high",
  bug: "normal",
  report_player: "high",
  suggestion: "low",
  other: "normal",
};

/** Transições permitidas à equipa. O jogador só responde (reabre) ou dá por resolvido — ver playerReply/playerResolve. */
const STAFF_TRANSITIONS: Record<Status, readonly Status[]> = {
  open: ["in_progress", "waiting_player", "resolved", "closed"],
  in_progress: ["open", "waiting_player", "resolved", "closed"],
  waiting_player: ["open", "in_progress", "resolved", "closed"],
  resolved: ["open", "in_progress", "closed"],
  closed: ["open"],
};

export function canStaffTransition(from: Status, to: Status): boolean {
  return from === to || STAFF_TRANSITIONS[from].includes(to);
}

/** Dias depois de resolvido em que o jogador ainda pode responder e reabrir; depois fecha sozinho. */
export const REOPEN_DAYS = 14;

/**
 * O que acontece quando o JOGADOR responde:
 * - à espera dele / em análise / aberto → volta a "aberto" (precisa de atenção da equipa);
 * - resolvido há menos de REOPEN_DAYS → reabre;
 * - fechado (ou resolvido há muito) → não pode: abre um pedido novo.
 */
export function playerReply(status: Status, resolvedAt: Date | null, now = new Date()): { ok: true; next: Status } | { ok: false; reason: "closed" } {
  if (status === "closed") return { ok: false, reason: "closed" };
  if (status === "resolved") {
    if (resolvedAt && now.getTime() - resolvedAt.getTime() > REOPEN_DAYS * 86_400_000) return { ok: false, reason: "closed" };
    return { ok: true, next: "open" };
  }
  return { ok: true, next: "open" };
}

/** Resposta pública da EQUIPA: por omissão fica "à espera do jogador"; pode escolher outro estado válido. */
export function staffReplyStatus(current: Status, requested?: Status): Status {
  const target = requested ?? (current === "resolved" || current === "closed" ? current : "waiting_player");
  return canStaffTransition(current, target) ? target : current;
}

/** Prazos (horas) por prioridade: primeira resposta e resolução. */
export const SLA_HOURS: Record<Priority, { firstResponse: number; resolution: number }> = {
  urgent: { firstResponse: 4, resolution: 24 },
  high: { firstResponse: 8, resolution: 48 },
  normal: { firstResponse: 24, resolution: 72 },
  low: { firstResponse: 72, resolution: 168 },
};

/** A partir de que fração do prazo consumido o pedido está "em risco". */
export const SLA_AT_RISK = 0.75;

export type SlaState = "ok" | "at_risk" | "breached" | "met" | "paused";

export type SlaInput = {
  priority: Priority;
  status: Status;
  createdAt: Date | string;
  firstResponseAt: Date | string | null;
  resolvedAt: Date | string | null;
};

const ms = (h: number) => h * 3_600_000;
const d = (x: Date | string) => new Date(x);

export function slaDeadlines(t: Pick<SlaInput, "priority" | "createdAt">) {
  const c = d(t.createdAt).getTime();
  const h = SLA_HOURS[t.priority];
  return { firstResponseDue: new Date(c + ms(h.firstResponse)), resolutionDue: new Date(c + ms(h.resolution)) };
}

function state(start: number, due: number, doneAt: number | null, now: number): SlaState {
  if (doneAt !== null) return doneAt <= due ? "met" : "breached";
  if (now > due) return "breached";
  return (now - start) / (due - start) >= SLA_AT_RISK ? "at_risk" : "ok";
}

/**
 * Estado dos prazos. Enquanto está "à espera do jogador", o prazo de resolução fica em pausa
 * (simplificação: não se desconta o tempo de espera do prazo — é uma pausa de apresentação).
 */
export function slaState(t: SlaInput, now = new Date()) {
  const { firstResponseDue, resolutionDue } = slaDeadlines(t);
  const start = d(t.createdAt).getTime();
  const n = now.getTime();
  const firstResponse = state(start, firstResponseDue.getTime(), t.firstResponseAt ? d(t.firstResponseAt).getTime() : null, n);
  const resolution: SlaState =
    t.status === "waiting_player" && !t.resolvedAt ? "paused" : state(start, resolutionDue.getTime(), t.resolvedAt ? d(t.resolvedAt).getTime() : null, n);
  /** O pior dos dois, para ordenar a fila e pintar o distintivo. */
  const order: SlaState[] = ["breached", "at_risk", "ok", "paused", "met"];
  const worst = [firstResponse, resolution].sort((a, b) => order.indexOf(a) - order.indexOf(b))[0]!;
  return { firstResponseDue, resolutionDue, firstResponse, resolution, worst };
}

/** Número visível do pedido: T-2026-0042. */
export function ticketNumber(year: number, seq: number): string {
  return `T-${year}-${String(seq).padStart(4, "0")}`;
}

export function isTicketNumber(s: string): boolean {
  return /^T-\d{4}-\d{4,}$/.test(s);
}

/** Anexos: só imagens, até 5 MB, no máximo 3 por mensagem. Reconhece o tipo pelos primeiros bytes (não pelo nome). */
export const ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
export const ATTACHMENTS_PER_MESSAGE = 3;

export function sniffImage(bytes: Uint8Array): "image/png" | "image/jpeg" | "image/webp" | "image/gif" | null {
  const b = bytes;
  if (b.length >= 8 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) return "image/png";
  if (b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return "image/jpeg";
  if (b.length >= 12 && b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46 && b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50) return "image/webp";
  if (b.length >= 6 && b[0] === 0x47 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x38) return "image/gif";
  return null;
}
